// ============================================================
// Auth API Service
// Connects to POST /api/v1/auth/login and GET /api/v1/auth/me
// Falls back to mock if backend is unreachable
// ============================================================

import { httpClient, setAuthToken, isApiReachable, ENABLE_MOCK_FALLBACK, ApiError } from './http-client';

export interface ApiStoreDTO {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  tier: string;
  /**
   * Label paket dari `plans.name` — sama seperti `plan_name` di
   * `stores-api.ts`. Dua DTO store yang berbeda memancarkan bentuk yang sama
   * dari endpoint yang berbeda, jadi keduanya harus membawanya; kalau cuma
   * satu, nama paket hilang tergantung dari mana store-nya dimuat.
   */
  plan_name?: string;
  category: string;
  description: string;
  status: string;
  trial_ends_at?: string;
  balance: number;
  monthly_budget?: number;
  team_count: number;
  max_team: number;
  ai_system_prompt?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginApiResponse {
  token: string;
  user: {
    id: string;
    email: string;
    full_name: string;
    role: string;
    avatar: string;
    is_internal_team?: boolean;
    is_early_adopter?: boolean;
  };
  stores?: ApiStoreDTO[];
}

export interface MeApiResponse {
  id: string;
  email: string;
  full_name: string;
  role: string;
  avatar: string;
  is_internal_team?: boolean;
  is_early_adopter?: boolean;
}

/**
 * Login via real backend API
 * POST /api/v1/auth/login
 */
export async function apiLogin(
  email: string,
  password: string
): Promise<LoginApiResponse> {
  console.log('[Auth API] 🔐 Starting login process', {
    email,
    endpoint: '/api/v1/auth/login',
    timestamp: new Date().toISOString(),
  });

  try {
    const res = await httpClient.post<LoginApiResponse>(
      '/api/v1/auth/login',
      { email, password }
    );

    console.log('[Auth API] Response received:', {
      success: res.success,
      hasData: !!res.data,
      hasToken: !!(res.data?.token),
      hasUser: !!(res.data?.user),
      hasStores: !!(res.data?.stores),
      storeCount: res.data?.stores?.length || 0,
      error: res.error,
    });

    if (!res.success || !res.data) {
      console.error('[Auth API] ❌ Login failed:', {
        success: res.success,
        error: res.error,
        response: res,
      });
      throw new ApiError(res.error || 'Login gagal', 401);
    }

    // Store JWT token
    console.log('[Auth API] ✅ Storing JWT token');
    setAuthToken(res.data.token);

    console.log('[Auth API] ✅ Login successful', {
      userId: res.data.user.id,
      userEmail: res.data.user.email,
      userName: res.data.user.full_name,
      storeCount: res.data.stores?.length || 0,
    });

    return res.data;
  } catch (error) {
    console.error('[Auth API] ❌ Login error caught:', {
      error,
      message: error instanceof Error ? error.message : 'Unknown error',
      isApiError: error instanceof ApiError,
      statusCode: error instanceof ApiError ? error.status : undefined,
    });
    throw error;
  }
}

/**
 * Get current user info via JWT
 * GET /api/v1/auth/me
 */
export async function apiGetMe(): Promise<MeApiResponse> {
  const res = await httpClient.get<MeApiResponse>('/api/v1/auth/me');

  if (!res.success || !res.data) {
    throw new ApiError(res.error || 'Gagal memuat data user', 401);
  }

  return res.data;
}

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  phone: string;
  tier: string;
}

/**
 * Register via real backend API
 * POST /api/v1/auth/register
 */
export async function apiRegister(
  data: RegisterRequest
): Promise<LoginApiResponse> {
  const res = await httpClient.post<LoginApiResponse>(
    '/api/v1/auth/register',
    data
  );

  if (!res.success || !res.data) {
    throw new ApiError(res.error || 'Registrasi gagal', 400);
  }

  // Store JWT token (auto-login)
  setAuthToken(res.data.token);

  return res.data;
}

/**
 * Logout - clear token
 */
export function apiLogout(): void {
  setAuthToken(null);
}

/**
 * Update user PIN (onboarding step 6)
 * PUT /api/v1/users/me/pin
 * 
 * Backend Status: ⚠️ NOT IMPLEMENTED YET
 * Will return 401/404 until backend implements this endpoint
 * See: docs/ONBOARDING_API_REQUIREMENTS.md
 */
export async function updateUserPin(pin: string): Promise<void> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.put('/api/v1/users/me/pin', { pin });
      if (!res.success) {
        throw new ApiError(res.error || 'Gagal update PIN', 500);
      }
      console.log('[API] ✅ PIN saved successfully');
      return;
    }

    if (ENABLE_MOCK_FALLBACK) {
      console.log('[API] ⚠️ Mock mode: PIN saved to localStorage only');
      return;
    }
    throw new ApiError('API tidak tersedia', 0);
  } catch (error) {
    // Gracefully handle 401/404 if endpoint not implemented yet
    if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
      console.warn('[API] ⚠️ PIN endpoint not implemented yet (returns', error.status, ')');
      console.warn('[API] 📝 See: docs/ONBOARDING_API_REQUIREMENTS.md for backend implementation');
      console.warn('[API] 💾 Falling back to localStorage for now');
      if (ENABLE_MOCK_FALLBACK) return; // Don't block user flow
      throw error;
    }
    
    if (error instanceof ApiError) throw error;
    if (ENABLE_MOCK_FALLBACK) {
      console.warn('[API] ⚠️ PIN save failed, continuing with localStorage fallback');
      return;
    }
    throw new ApiError(error instanceof Error ? error.message : 'Gagal update PIN', 0);
  }
}

/**
 * Tukar token handoff sekali-pakai jadi sesi penuh (SUBSC-20).
 *
 * POST /api/v1/auth/exchange — publik, karena pemanggilnya memang belum punya
 * sesi; itu justru intinya. Responsnya bentuknya sama persis dengan
 * `/auth/login`, jadi `applyLoginResponse` bisa dipakai apa adanya.
 *
 * Sengaja TIDAK mencetak token ke console, tidak seperti `apiLogin` yang
 * mencatat email. Token ini adalah kredensial hidup sampai ditukar, dan log
 * browser ikut tersalin saat pengguna mengirimkan tangkapan layar.
 */
export async function apiExchangeToken(token: string): Promise<LoginApiResponse> {
  const res = await httpClient.post<LoginApiResponse>('/api/v1/auth/exchange', { token });

  if (!res.success || !res.data) {
    // Backend sengaja mengembalikan satu pesan generik untuk kedaluwarsa,
    // sudah dipakai, dan tidak pernah ada — supaya penebak token tidak bisa
    // membedakan ketiganya. Diteruskan apa adanya.
    throw new ApiError(res.error || 'Link masuk ini sudah tidak berlaku. Silakan login kembali.', 401);
  }
  return res.data;
}
